import { type CertificationPeriodViewModel } from '@generated';
import type { CalendarEventModel } from '~/shared/calendar/types';
import { MultiMonthCalendar, type MultiMonthCalendarProps } from '~/shared/calendar/MultiMonthCalendar';
import { addDays, dfIsSameDay } from '~/util/date';
import type { Day } from 'date-fns';
import { useAgencyStartOfWeek } from '~/libraries/agencies/queries';

function getEpisodeDayLabels(date: Date, certificationPeriodStartDate: Date, certificationPeriodEndDate: Date) {
	const secondPeriodStartDate = addDays(certificationPeriodStartDate, 30);
	if (dfIsSameDay(date, certificationPeriodStartDate)) {
		return 'Ep. Start';
	}
	if (dfIsSameDay(date, secondPeriodStartDate)) {
		return '2nd Per';
	}
	if (dfIsSameDay(date, certificationPeriodEndDate)) {
		return 'Ep. End';
	}
	return undefined;
}

export function CertificationPeriodCalendar<T extends CalendarEventModel>({
	certificationPeriod,
	...props
}: { certificationPeriod: CertificationPeriodViewModel } & Omit<MultiMonthCalendarProps<T>, 'startDate' | 'endDate' | 'weekStartOverride'>) {
	const agencyStartOfWeek = useAgencyStartOfWeek();

	return (
		<MultiMonthCalendar
			startDate={certificationPeriod.startDate}
			endDate={certificationPeriod.endDate}
			weekStartOverride={agencyStartOfWeek as Day}
			{...props}
			label={
				props.label ??
				`Cert. Period #${
					certificationPeriod.certificationNumber
				} (${certificationPeriod.startDate.toLocaleDateString()} - ${certificationPeriod.endDate.toLocaleDateString()})`
			}
			renderDayLabel={date => getEpisodeDayLabels(date, certificationPeriod.startDate, certificationPeriod.endDate)}
		/>
	);
}
